import { Suspense } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  ServiceIcons,
  SkeletonText,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import {
  agentAtomFamily_SUSPENDS,
  AgentFetchResult,
  assetAccessAtomFamily_SUSPENDS,
  assetStepTitlePlaceholderTextAtom,
  isViewOnlyModeAtom,
  nodeLabelAtomFamily,
  isNodeSelectedAtomFamily,
  nodeActorAtomFamily,
} from "@zapier/canvas-state";
import { getUniqueAgentWorkflowApps } from "../../../../../../utils/getUniqueAgentWorkflowApps";
import useServiceById from "../../../../../../hooks/useServiceById";
import cleanUpAppId from "../../../../../../utils/cleanUpAppId";
import getAppIconUrl from "../../../../../../utils/getAppIconUrl";
import { StepTitle } from "../../../../../StepTitle";
import { ActorLabelPositioner } from "../../../../../ActorLabelPositioner";
import styles from "./AgentThumbnail.module.css";

type Props = {
  id: string;
  agentId: string;
};

/** Special agent apps that implementations API don't have data for
 * Copied from https://gitlab.com/zapier/zapai/ai-command-center/-/blob/staging/apps/mcp/src/components/workflows/workflow-control/app-logo.tsx
 * */
const NAME_MAP: Record<string, string> = {
  zapier_zap_trigger: "Zap",
  zapier_bot_dm_trigger: "On demand",
};

const ZAP_SVG_SRC =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="2 2 20 20"><path fill="%23FF4F00" d="M11 12H7.74996L13 5.75V12H16.25L11 18.25V16H8.99996V23.75L20.54 10H15V0.25L3.45996 14H11V12Z"/></svg>';

const ON_DEMAND_SVG_SRC =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="2 2 20 20"><path fill="%23503ebd" d="M5 20.37L19.5 12L5 3.63V20.37ZM7 7.09L15.5 12L7 16.91V7.09Z"/></svg>';

const SRC_MAP: Record<string, string> = {
  zapier_zap_trigger: ZAP_SVG_SRC,
  zapier_bot_dm_trigger: ON_DEMAND_SVG_SRC,
};

function SuspendedAgentThumbnail({ id, agentId }: Props) {
  const label = useAtomValue(nodeLabelAtomFamily(id));
  const isSelected = useAtomValue(isNodeSelectedAtomFamily(id));
  const actor = useAtomValue(nodeActorAtomFamily(id));
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const { isAssetForbidden } = useAtomValue(
    assetAccessAtomFamily_SUSPENDS({
      assetId: agentId,
      assetType: "agent",
    }),
  );
  const getPlaceholder = useSetAtom(assetStepTitlePlaceholderTextAtom);
  const placeholder = getPlaceholder("agent");
  const isViewOnlyOrForbidden = isViewOnlyMode || isAssetForbidden;

  const agentResult = useAtomValue(agentAtomFamily_SUSPENDS(agentId)) as
    | AgentFetchResult
    | null
    | undefined;
  const getService = useServiceById();

  // Determine view state (treat null as generic error/unavailable)
  const isSuccess = agentResult?.type === "success";

  /* ------------------------------------------------------------
   * Success ‑ hydrate workflow app icons
   * ---------------------------------------------------------- */
  const agent = isSuccess ? agentResult.data : undefined;

  const uniqueWorkflowAppsHydrated = isSuccess
    ? getUniqueAgentWorkflowApps(agent!.workflow).map((appId) => {
        const size = "xx-small";
        const service = getService(appId);
        const images = service?.images;
        const name = NAME_MAP[appId] ?? service?.name ?? cleanUpAppId(appId);
        const src = SRC_MAP[appId] ?? getAppIconUrl(appId, images, size);

        return {
          name,
          src,
        };
      })
    : [];

  const serviceNames = uniqueWorkflowAppsHydrated
    .map((app) => app.name)
    .join(", ");

  return (
    <div className={styles.root}>
      <div className={styles.flexRow}>
        {isSuccess && Boolean(uniqueWorkflowAppsHydrated.length) && (
          <div className={styles.tooltipContainer}>
            <Tooltip placement="top" content={serviceNames}>
              <TooltipFocusableTrigger>
                <ServiceIcons
                  hasBorder
                  maxServices={3}
                  size="small"
                  services={uniqueWorkflowAppsHydrated}
                />
              </TooltipFocusableTrigger>
            </Tooltip>
          </div>
        )}
        {actor && <ActorLabelPositioner actor={actor} shouldOverlap={false} />}
      </div>
      <StepTitle
        nodeId={id}
        label={label}
        isSelected={isSelected}
        placeholder={isViewOnlyOrForbidden ? "" : placeholder}
        canNodeGetAiSuggestion={false}
        rows={2}
        isViewOnlyMode={isViewOnlyOrForbidden}
        isBlankAgentNode={false}
        shouldHaveCharLimit={false}
      />
    </div>
  );
}

function AgentThumbnailLoader() {
  return (
    <SkeletonText
      lines={3}
      size="body"
      textAlign="left"
      width="100%"
      color="GrayWarm3"
    />
  );
}

export function AgentThumbnail({ id, agentId }: Props) {
  return (
    <Suspense fallback={<AgentThumbnailLoader />}>
      <SuspendedAgentThumbnail id={id} agentId={agentId} />
    </Suspense>
  );
}
